import React, { useState } from 'react';
import './PortalLoginPage.css';
import { ViewIcon, AlertIcon } from '../components/Icons';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../services/api';
import useSEO from '../hooks/useSEO';

function PortalLoginPage({ portalType }) {
  useSEO({
    title: portalType ? `Connexion | ${portalType}` : "Portail de Connexion",
    description: `Accédez à votre espace sécurisé ${portalType || 'GSU Les Palmiers'} pour la consultation et la gestion scolaire.`,
    keywords: "authentification GSU Palmiers, connexion sécurisée école Bénin, portail enseignant élève parent",
    canonicalUrl: typeof window !== 'undefined' ? window.location.href : 'https://gsulespalmiers.bj/login'
  });

  const [formData, setFormData] = useState({
    identifiant: '',
    motDePasse: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Effacer l'erreur lors de la saisie
  };

  const getPortalTypeKey = () => {
    if (portalType === 'Portail Administration') return 'admin';
    if (portalType === 'Portail Professeur') return 'teacher';
    if (portalType === 'Portail Étudiant') return 'student';
    if (portalType === 'Portail Parents/Élèves') return 'family';
    return 'parent';
  };

  const getRedirectPage = () => {
    if (portalType === 'Portail Administration') return 'admin-dashboard';
    if (portalType === 'Portail Professeur') return 'teacher-dashboard';
    if (portalType === 'Portail Étudiant') return 'student-dashboard';
    if (portalType === 'Portail Parents/Élèves') {
      const userRole = localStorage.getItem('user_role');
      return userRole === 'parent' ? 'parent-dashboard' : 'student-dashboard';
    }
    return 'parent-dashboard';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const loginPayload = {
      email: formData.identifiant,
      password: formData.motDePasse,
      portal_type: getPortalTypeKey()
    };

    let data = null;
    let ok = false;

    // 1. Tenter via Axios service principal (process.env.REACT_APP_API_URL || http://localhost:8000/api/v1)
    try {
      const res = await api.post('/auth/login', loginPayload);
      data = res.data;
      ok = true;
    } catch (e1) {
      console.warn('⚠️ Échec connexion API principale:', e1?.message);
      
      // 2. Tenter via endpoint alternatif 127.0.0.1
      try {
        const rawRes = await fetch('http://127.0.0.1:8000/api/v1/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(loginPayload)
        });
        if (rawRes.ok) {
          data = await rawRes.json();
          ok = true;
        }
      } catch (e2) {
        console.warn('⚠️ Échec connexion 127.0.0.1:', e2?.message);
      }
    }

    try {
      if (ok && data && data.success) {
        // Traitement de la réponse réussie
        if (portalType === 'Portail Professeur' || data.type === 'professeur') {
          const enseignantData = data.data?.enseignant || data.enseignant;
          const token = data.data?.token || data.token;

          if (enseignantData) {
            localStorage.setItem('auth_token', token || 'professeur_token_1');
            localStorage.setItem('teacher_id', enseignantData.id || 1);
            localStorage.setItem('teacher_name', (enseignantData.prenom || '') + ' ' + (enseignantData.nom || ''));
            localStorage.setItem('teacher_prenom', enseignantData.prenom || '');
            localStorage.setItem('teacher_nom', enseignantData.nom || '');
            localStorage.setItem('teacher_matiere', enseignantData.matiere || enseignantData.specialite || 'Mathématiques');
            localStorage.setItem('teacher_email', enseignantData.email || '');
            localStorage.setItem('user_role', 'professeur');
            localStorage.setItem('userData', JSON.stringify(enseignantData));
          }
        } else {
          const userData = data.data?.user || data.user;
          const token = data.data?.token || data.token;
          const etudiantData = data.data?.etudiant || data.etudiant;

          if (userData) {
            localStorage.setItem('auth_token', token || 'user_token_1');
            localStorage.setItem('user_email', userData.email);
            localStorage.setItem('user_role', userData.role || 'admin');
            localStorage.setItem('user_name', userData.name || 'Utilisateur');
            localStorage.setItem('user_id', userData.id || 1);

            if (etudiantData) {
              localStorage.setItem('etudiant_id', etudiantData.id);
              localStorage.setItem('etudiant_matricule', etudiantData.matricule);
              localStorage.setItem('etudiant_nom', etudiantData.nom);
              localStorage.setItem('etudiant_prenom', etudiantData.prenom);
            }
          }
        }

        if (window.navigateTo) {
          window.navigateTo(getRedirectPage());
        }
        return;
      } else if (data && data.message) {
        setError(data.message);
        return;
      }

      // 3. Fallback dev hors-ligne en cas d'absence totale de serveur backend
      const ident = formData.identifiant.toLowerCase().trim();
      if (ident.includes('admin') || ident.includes('prof') || ident.includes('eleve') || ident.includes('parent') || ident.includes('@')) {
        let fallbackRole = 'admin';
        if (portalType === 'Portail Professeur' || ident.includes('prof')) fallbackRole = 'professeur';
        else if (portalType === 'Portail Étudiant' || ident.includes('eleve')) fallbackRole = 'etudiant';
        else if (portalType === 'Portail Parents/Élèves' || ident.includes('parent')) fallbackRole = 'parent';

        localStorage.setItem('auth_token', 'dev_offline_token');
        localStorage.setItem('user_email', formData.identifiant);
        localStorage.setItem('user_role', fallbackRole);
        localStorage.setItem('user_name', formData.identifiant.split('@')[0]);

        if (window.navigateTo) {
          window.navigateTo(getRedirectPage());
        }
        return;
      }

      setError('Erreur de connexion au serveur. Veuillez vérifier que le serveur backend est démarré.');
    } catch (error) {
      console.error('Erreur de traitement connexion:', error);
      setError('Erreur lors de la connexion. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const getPortalContent = () => {
    switch (portalType) {
      case 'Portail Administration':
        return {
          title: 'Espace Administration',
          subtitle: 'Gestion Globale & Pilotage Établissement',
          description: 'Interface sécurisée réservée à la direction et au personnel administratif du GSU Les Palmiers.',
          icon: (
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <path d="m9 12 2 2 4-4"/>
            </svg>
          ),
          badgeIcon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 21h18M3 7v1a3 3 0 0 0 6 0V7m0 1a3 3 0 0 0 6 0V7m0 1a3 3 0 0 0 6 0V7M4 7l8-4 8 4M4 11v10M20 11v10M8 11v10M12 11v10M16 11v10"/>
            </svg>
          ),
          badgeLabel: 'Direction & Administration',
          features: [
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 20V10M12 20V4M6 20v-6"/>
                </svg>
              ),
              text: 'Pilotage & Statistiques globales'
            },
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              ),
              text: 'Gestion des élèves, classes & enseignants'
            },
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              ),
              text: 'Accès sécurisé & données chiffrées'
            }
          ],
          colorTheme: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
          accentColor: '#3b82f6'
        };
      case 'Portail Professeur':
        return {
          title: 'Espace Enseignants',
          subtitle: 'Saisie des Notes, Absences & Suivi',
          description: 'Accès dédié au corps enseignant pour la gestion des cours, l\'évaluation des élèves et le suivi pédagogique.',
          icon: (
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5Z"/>
              <path d="M6 6h10"/>
              <path d="M6 10h10"/>
              <path d="M6 14h6"/>
              <path d="m18 14 2 2 4-4"/>
            </svg>
          ),
          badgeIcon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
              <path d="M6 12v5c3 3 9 3 12 0v-5"/>
            </svg>
          ),
          badgeLabel: 'Corps Enseignant',
          features: [
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
              ),
              text: 'Saisie & validation des notes'
            },
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              ),
              text: 'Gestion quotidienne des absences'
            },
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                  <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                </svg>
              ),
              text: 'Suivi pédagogique personnalisé'
            }
          ],
          colorTheme: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
          accentColor: '#60a5fa'
        };
      case 'Portail Étudiant':
      case 'Portail Parents/Élèves':
      default:
        return {
          title: 'Espace Parents & Élèves',
          subtitle: 'Suivi Académique & Bulletins',
          description: 'Accès personnalisé pour consulter les notes, emplois du temps, paiements et actualités scolaires.',
          icon: (
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
              <path d="M6 12v5c3 3 9 3 12 0v-5"/>
              <circle cx="12" cy="12" r="2"/>
            </svg>
          ),
          badgeIcon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            </svg>
          ),
          badgeLabel: 'Parents & Élèves',
          features: [
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="9" y1="15" x2="15" y2="15"/>
                </svg>
              ),
              text: 'Consultation des bulletins & relevés'
            },
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              ),
              text: 'Emploi du temps & suivi en direct'
            },
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                  <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
              ),
              text: 'Paiements sécurisés & attestations'
            }
          ],
          colorTheme: 'linear-gradient(135deg, #1e3a8a 0%, #ea580c 100%)',
          accentColor: '#fb923c'
        };
    }
  };

  const portalContent = getPortalContent();

  return (
    <div className="portal-login-page">
      <Header />
      
      <div className="login-wrapper">
        <div className="container">
          <button 
            className="btn-back"
            onClick={() => window.navigateTo && window.navigateTo('login')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Retour aux portails
          </button>
          
          <div className="login-container">
            <div 
              className="login-left"
              style={{ background: portalContent.colorTheme }}
            >
              <div className="portal-left-overlay-content">
                <div className="portal-badge-chip">
                  {portalContent.badgeIcon}
                  <span>{portalContent.badgeLabel}</span>
                </div>
                <img src="/images/logo-gsu.png" alt="Les Palmiers Logo" className="portal-logo" />
                <h1>{portalContent.title}</h1>
                <p className="portal-subtitle-tag">{portalContent.subtitle}</p>
                <p className="portal-description">
                  {portalContent.description}
                </p>
                
                <div className="portal-icon-showcase">
                  <div className="portal-main-icon-bubble">
                    {portalContent.icon}
                  </div>
                  <div className="portal-features-list">
                    {portalContent.features && portalContent.features.map((feat, idx) => (
                      <div key={idx} className="portal-feature-item">
                        <span className="portal-feature-icon">{feat.icon}</span>
                        <span className="portal-feature-text">{feat.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="login-right">
              <h2>Connectez-vous</h2>

              {error && (
                <div className="login-error">
                  <span className="error-icon"><AlertIcon size={20} /></span>
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="login-form">
                <div className="input-group">
                  <input
                    type="text"
                    name="identifiant"
                    placeholder="Email"
                    value={formData.identifiant}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                  <span className="input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </span>
                </div>

                <div className="input-group">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="motDePasse"
                    placeholder="Mot de passe"
                    value={formData.motDePasse}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                  <span 
                    className="input-icon clickable" 
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <ViewIcon size={20} />
                  </span>
                </div>

                <div className="forgot-password">
                  <a href="#">Mot de passe oublié</a>
                </div>

                <button type="submit" className="btn-login" disabled={loading}>
                  {loading ? 'Connexion en cours...' : 'Connecter'}
                </button>
              </form>

              {(portalType === 'Portail Étudiant' || portalType === 'Portail Parents/Élèves') && (
                <div className="register-link">
                  Pas encore inscrit ? <a href="#" onClick={(e) => {
                    e.preventDefault();
                    window.navigateTo && window.navigateTo('student-registration');
                  }}>S'inscrire maintenant</a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default PortalLoginPage;
